import type { Comment, Feedback } from '../../types'
import { CHANNEL_WORDS, FEEDBACK_CHANNELS, isReadyComment, ratingSummary } from '../../utils'
import type { FeedbackChannel, RatingSummary } from '../../utils'
import { COLOURS } from '../charts/theme'

// Below this many written comments the cloud says too little: quotes only.
export const MIN_COMMENTS_FOR_CLOUD = 10
export const COMMENTS_PER_PAGE = 20
// Two beside the cloud on each side and two under it.
export const QUOTES_SHOWN = 6
export const TOP_WORDS = 15

// What the overall rating is built from: both channels, one, or none yet.
export const reviewScope = (rated: FeedbackChannel[]): string => {
  if (rated.length === 0) return 'reviews'
  if (rated.length > 1) return 'reviews of coaching and workshops'
  return `${CHANNEL_WORDS[rated[0]].noun} reviews`
}

// The channels behind the overall rating, as utils.ratingSummary picks them
// for the Overview too: enough different people, and an average.
export const ratedChannels = (feedback: Feedback): FeedbackChannel[] => {
  const summary = ratingSummary(feedback)
  return summary.state === 'rated' ? summary.channels : []
}

export type FeedbackMode = 'none' | 'hidden' | 'ratings'

const MODES: Record<RatingSummary['state'], FeedbackMode> = { none: 'none', hidden: 'hidden', rated: 'ratings' }

// Nobody has reviewed; no channel has enough people yet; or rated.
export const feedbackMode = (feedback: Feedback): FeedbackMode => MODES[ratingSummary(feedback).state]

// Reviews behind the overall rating: only the qualifying channels'.
export const ratedReviews = (feedback: Feedback): number => {
  const summary = ratingSummary(feedback)
  return summary.state === 'rated' ? summary.reviews : 0
}

// People behind the overall rating. With both channels rated it is everyone
// (someone can review both, so the channels' people don't add up); with one,
// that channel's people.
export const ratedPeople = (feedback: Feedback): number => {
  const rated = ratedChannels(feedback)
  if (rated.length === FEEDBACK_CHANNELS.length) return feedback.people
  return rated.reduce((total, channel) => total + feedback.channels[channel].people, 0)
}

export const readyComments = (comments: Comment[]): Comment[] => comments.filter(isReadyComment)

// The newest ready comments (the server sends newest first).
export const featuredQuotes = (comments: Comment[], count = QUOTES_SHOWN): Comment[] => readyComments(comments).slice(0, count)

export type CommentsView = 'cloud' | 'quotes' | 'preparing'

// The cloud once there are enough written comments (and words to draw);
// "preparing" while every comment so far is still being prepared; else quotes.
export const commentsView = (comments: Comment[], keywordCount: number): CommentsView => {
  const ready = readyComments(comments).length
  if (ready >= MIN_COMMENTS_FOR_CLOUD && keywordCount > 0) return 'cloud'
  if (ready === 0 && comments.some((comment) => comment.state === 'preparing')) return 'preparing'
  return 'quotes'
}

// Case-insensitive. A comment still being prepared has no text to match.
export const matchComments = (comments: Comment[], query: string): Comment[] => {
  const needle = query.trim().toLowerCase()
  if (!needle) return comments

  return comments.filter((comment) => comment.text?.toLowerCase().includes(needle))
}

export const pageCount = (total: number, perPage = COMMENTS_PER_PAGE): number => Math.max(1, Math.ceil(total / perPage))

export const pageOf = <T>(items: T[], page: number, perPage = COMMENTS_PER_PAGE): T[] =>
  items.slice(page * perPage, (page + 1) * perPage)

// "Showing 21–40 of 45 comments"
export const showingLabel = (page: number, total: number, perPage = COMMENTS_PER_PAGE): string => {
  if (total === 0) return 'Showing 0 comments'

  const first = page * perPage + 1
  const last = Math.min(total, (page + 1) * perPage)
  return `Showing ${first}–${last} of ${total} comments`
}

// The cloud's colour for each word, orange when it is critical. SVG fill
// can't read CSS variables.
export const wordColour = (keywords: Feedback['keywords']): ((word: { text: string }) => string) => {
  const critical = new Set(keywords.filter((keyword) => keyword.critical).map((keyword) => keyword.word))
  return (word) => (critical.has(word.text) ? COLOURS.criticalWord : COLOURS.forestDark)
}

export const topWords = (keywords: Feedback['keywords'], count = TOP_WORDS): Feedback['keywords'] =>
  [...keywords].sort((a, b) => b.count - a.count || a.word.localeCompare(b.word)).slice(0, count)
